import { createClient } from '@supabase/supabase-js'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || 'https://placeholder.supabase.co'
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY || 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.placeholder'

export const supabase = createClient(supabaseUrl, supabaseAnonKey)

// Aparte client om NIEUWE accounts aan te maken zonder de huidige (admin-)sessie
// te overschrijven. signUp logt anders automatisch in als de nieuwe gebruiker,
// waardoor de daaropvolgende members-insert niet meer als beheerder draait.
const signupClient = createClient(supabaseUrl, supabaseAnonKey, {
  auth: { persistSession: false, autoRefreshToken: false, storageKey: 'sb-signup-temp' },
})

export async function createMemberAccount(email, password, metadata) {
  const { data, error } = await signupClient.auth.signUp({
    email,
    password,
    options: { data: metadata },
  })
  if (error) throw error
  return data?.user?.id
}

// ── Configuratie ──────────────────────────────────────
// Emaildomein voor leden — pas hier aan als het domein verandert
export const LID_EMAIL_DOMEIN = 'vitaal-sterk.nl'
export const lidEmail = (memberId) => `${memberId.toLowerCase().trim()}@${LID_EMAIL_DOMEIN}`

export const isConfigured = Boolean(
  process.env.REACT_APP_SUPABASE_URL &&
  !process.env.REACT_APP_SUPABASE_URL.includes('placeholder')
)

// ── Auth ──────────────────────────────────────────────
export async function signInWithMemberId(memberIdOrEmail, pin) {
  // Accepteer zowel lidnummer (LID-0001) als volledig emailadres
  const email = memberIdOrEmail.includes('@')
    ? memberIdOrEmail
    : lidEmail(memberIdOrEmail)
  const { data, error } = await supabase.auth.signInWithPassword({ email, password: pin })
  if (error) throw error
  return data
}

export async function signOut() {
  await supabase.auth.signOut()
}

export function onAuthStateChange(cb) {
  return supabase.auth.onAuthStateChange((_event, session) => cb(session))
}

// ── Members ───────────────────────────────────────────
export async function getMyProfile(userId) {
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .eq('user_id', userId)
    .single()
  if (error) throw error
  return data
}

export async function getAllMembers() {
  const { data, error } = await supabase
    .from('members')
    .select('*')
    .order('name', { ascending: true })
  if (error) throw error
  return data ?? []
}

export async function toggleMemberStatus(id, status) {
  const { error } = await supabase
    .from('members')
    .update({ status: status === 'active' ? 'inactive' : 'active' })
    .eq('id', id)
  if (error) throw error
}

// ── Newsletters ───────────────────────────────────────
export const MAX_ACTIEVE_NIEUWSBERICHTEN = 12

// Actieve (niet-gearchiveerde) nieuwsberichten — dit is wat leden te zien krijgen,
// dus nooit meer dan het maximum.
export async function getNewsletters() {
  const { data, error } = await supabase
    .from('newsletters')
    .select('*')
    .eq('gearchiveerd', false)
    .order('created_at', { ascending: false })
    .limit(MAX_ACTIEVE_NIEUWSBERICHTEN)
  if (error) throw error
  return data
}

// Gearchiveerde nieuwsberichten — alleen zichtbaar in het beheerderspaneel.
export async function getGearchiveerdeNewsletters() {
  const { data, error } = await supabase
    .from('newsletters')
    .select('*')
    .eq('gearchiveerd', true)
    .order('created_at', { ascending: false })
  if (error) throw error
  return data
}

// Archiveert automatisch de oudste actieve berichten zodra het maximum wordt overschreden
// (bij publiceren of bij het terugzetten van een gearchiveerd bericht).
async function handhaafNieuwsMaximum() {
  const { data, error } = await supabase
    .from('newsletters')
    .select('id')
    .eq('gearchiveerd', false)
    .order('created_at', { ascending: false })
  if (error || !data) return
  const teArchiveren = data.slice(MAX_ACTIEVE_NIEUWSBERICHTEN).map(n => n.id)
  if (teArchiveren.length > 0) {
    await supabase.from('newsletters').update({ gearchiveerd: true }).in('id', teArchiveren)
  }
}

export async function publishNewsletter({ title, category, body, foto_url }) {
  const { error } = await supabase.from('newsletters').insert({ title, category, body, foto_url: foto_url || null })
  if (error) throw error
  await handhaafNieuwsMaximum()
}

export async function updateNewsletter(id, { title, category, body, foto_url }) {
  const { error } = await supabase
    .from('newsletters')
    .update({ title, category, body, foto_url: foto_url || null })
    .eq('id', id)
  if (error) throw error
}

export async function archiveerNewsletter(id) {
  const { error } = await supabase.from('newsletters').update({ gearchiveerd: true }).eq('id', id)
  if (error) throw error
}

export async function herstelNewsletter(id) {
  const { error } = await supabase
    .from('newsletters')
    .update({ gearchiveerd: false, created_at: new Date().toISOString() })
    .eq('id', id)
  if (error) throw error
  await handhaafNieuwsMaximum()
}

export async function uploadNieuwsFoto(file) {
  const ext = file.name.split('.').pop()
  const path = `nieuws/${Date.now()}.${ext}`
  const { error } = await supabase.storage
    .from('Profielfoto')
    .upload(path, file, { upsert: false, contentType: file.type })
  if (error) throw error
  const { data } = supabase.storage.from('Profielfoto').getPublicUrl(path)
  return data.publicUrl
}

export async function deleteNewsletter(id) {
  const { error } = await supabase.from('newsletters').delete().eq('id', id)
  if (error) throw error
}

// Registreer dat een lid een nieuwsbrief heeft geopend (1x per lid per bericht)
export async function markNewsletterRead(newsletterId, memberId) {
  if (!newsletterId || !memberId) return
  try {
    await supabase.from('newsletter_reads').upsert(
      { newsletter_id: newsletterId, member_id: memberId },
      { onConflict: 'newsletter_id,member_id', ignoreDuplicates: true }
    )
  } catch { /* stil — mag de leeservaring nooit blokkeren */ }
}

// Aantal leden dat elke nieuwsbrief heeft geopend -> { newsletter_id: aantal }
// Telt in de database zelf (get_newsletter_read_counts), i.p.v. alle losse
// newsletter_reads-rijen op te halen en in de browser te tellen — dat liep
// tegen Supabase's standaard max-rows-per-verzoek (1000) aan zodra er meer
// leesregels waren dan dat, waardoor recente berichten onterecht op 0 stonden.
export async function getNewsletterReadCounts() {
  const { data, error } = await supabase.rpc('get_newsletter_read_counts')
  if (error) return {}
  const counts = {}
  ;(data ?? []).forEach(r => { counts[r.newsletter_id] = Number(r.aantal) })
  return counts
}

// ── Messages ──────────────────────────────────────────
export async function getMessages(memberId) {
  const { data, error } = await supabase
    .from('messages')
    .select('*')
    .eq('member_id', memberId)
    .order('created_at', { ascending: true })
  if (error) throw error
  return data
}

export async function sendMessage({ memberId, fromAdmin, text }) {
  const { error } = await supabase.from('messages').insert({
    member_id: memberId,
    from_admin: fromAdmin,
    text,
  })
  if (error) throw error
}

export async function sendBulkMessage(text) {
  const { data: members, error } = await supabase
    .from('members')
    .select('member_id')
    .eq('status', 'actief')
  if (error) throw error

  const inserts = members.map(m => ({
    member_id: m.member_id,
    from_admin: true,
    text,
  }))

  const { error: insertError } = await supabase.from('messages').insert(inserts)
  if (insertError) throw insertError

  return members.length
}

export function subscribeToMessages(memberId, cb) {
  return supabase
    .channel(`messages:${memberId}`)
    .on('postgres_changes', {
      event: 'INSERT',
      schema: 'public',
      table: 'messages',
      filter: `member_id=eq.${memberId}`,
    }, payload => cb(payload.new))
    .subscribe()
}

// ── Profielfoto ───────────────────────────────────────
export async function uploadProfielfoto(userId, file) {
  // Verklein de afbeelding naar max 400x400 en zet om naar JPEG
  const verkleind = await verkleenAfbeelding(file, 400)

  // Vaste bestandsnaam per gebruiker zodat upsert altijd hetzelfde bestand overschrijft
  const path = `avatars/${userId}.jpg`

  const { error: uploadError } = await supabase.storage
    .from('Profielfoto')
    .upload(path, verkleind, { upsert: true, contentType: 'image/jpeg' })

  if (uploadError) throw uploadError

  const { data } = supabase.storage
    .from('Profielfoto')
    .getPublicUrl(path)

  // Cache-buster consistent opslaan én teruggeven
  const urlMetTimestamp = data.publicUrl + '?t=' + Date.now()

  const { error: updateError } = await supabase
    .from('members')
    .update({ avatar_url: urlMetTimestamp })
    .eq('user_id', userId)

  if (updateError) throw updateError
  return urlMetTimestamp
}

function verkleenAfbeelding(file, maxPx) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    const url = URL.createObjectURL(file)
    img.onload = () => {
      URL.revokeObjectURL(url)
      const schaal = Math.min(1, maxPx / Math.max(img.width, img.height))
      const canvas = document.createElement('canvas')
      canvas.width = Math.round(img.width * schaal)
      canvas.height = Math.round(img.height * schaal)
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height)
      canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('Canvas toBlob mislukt')), 'image/jpeg', 0.85)
    }
    img.onerror = reject
    img.src = url
  })
}

// ── Team ──────────────────────────────────────────────
export async function getTeamleden() {
  const { data, error } = await supabase
    .from('team')
    .select('*')
    .order('volgorde', { ascending: true })
  if (error) throw error
  return data
}

export async function upsertTeamlid(lid) {
  const { error } = await supabase
    .from('team')
    .upsert(lid, { onConflict: 'id' })
  if (error) throw error
}

export async function deleteTeamlid(id) {
  const { error } = await supabase.from('team').delete().eq('id', id)
  if (error) throw error
}

export async function uploadTeamFoto(id, file) {
  const ext = file.name.split('.').pop()
  const path = `team/${id}.${ext}`
  const { error } = await supabase.storage
    .from('Profielfoto')
    .upload(path, file, { upsert: true, contentType: file.type })
  if (error) throw error
  const { data } = supabase.storage.from('Profielfoto').getPublicUrl(path)
  return data.publicUrl + '?t=' + Date.now()
}
// ── Stempelkaart ───────────────────────────────────────
export async function getStempelkaart(memberId, jaar, maand) {
  const { data, error } = await supabase
    .from('stempelkaarten')
    .select('*')
    .eq('member_id', memberId)
    .eq('jaar', jaar)
    .eq('maand', maand)
    .single()
  if (error && error.code !== 'PGRST116') throw error
  return data
}

export async function getBezoeken(memberId, jaar, maand) {
  const start = new Date(jaar, maand - 1, 1).toISOString()
  const end = new Date(jaar, maand, 1).toISOString()
  const { data, error } = await supabase
    .from('bezoeken')
    .select('*')
    .eq('member_id', memberId)
    .gte('bezocht_op', start)
    .lt('bezocht_op', end)
    .order('bezocht_op', { ascending: true })
  if (error) throw error
  return data
}

export async function addStempel(memberId) {
  const now = new Date()
  const jaar = now.getFullYear()
  const maand = now.getMonth() + 1

  // Voeg bezoek toe
  const { error: bezoekError } = await supabase
    .from('bezoeken')
    .insert({ member_id: memberId, bezocht_op: now.toISOString() })
  if (bezoekError) throw bezoekError

  // Upsert stempelkaart
  const { data: existing } = await supabase
    .from('stempelkaarten')
    .select('*')
    .eq('member_id', memberId)
    .eq('jaar', jaar)
    .eq('maand', maand)
    .single()

  if (existing) {
    const { error } = await supabase
      .from('stempelkaarten')
      .update({ gebruikt: existing.gebruikt + 1 })
      .eq('id', existing.id)
    if (error) throw error
  } else {
    const { error } = await supabase
      .from('stempelkaarten')
      .insert({ member_id: memberId, jaar, maand, gebruikt: 1, maximum: 8 })
    if (error) throw error
  }
}

export async function removeStempel(memberId) {
  const now = new Date()
  const jaar = now.getFullYear()
  const maand = now.getMonth() + 1

  const { data: existing } = await supabase
    .from('stempelkaarten')
    .select('*')
    .eq('member_id', memberId)
    .eq('jaar', jaar)
    .eq('maand', maand)
    .single()

  if (!existing || existing.gebruikt <= 0) return

  // Verwijder laatste bezoek
  const start = new Date(jaar, maand - 1, 1).toISOString()
  const end = new Date(jaar, maand, 1).toISOString()
  const { data: bezoeken } = await supabase
    .from('bezoeken')
    .select('id, bezocht_op')
    .eq('member_id', memberId)
    .gte('bezocht_op', start)
    .lt('bezocht_op', end)
    .order('bezocht_op', { ascending: false })
    .limit(1)

  if (bezoeken?.length > 0) {
    await supabase.from('bezoeken').delete().eq('id', bezoeken[0].id)
  }

  await supabase
    .from('stempelkaarten')
    .update({ gebruikt: existing.gebruikt - 1 })
    .eq('id', existing.id)
}

export async function updateMaximum(memberId, maximum) {
  const now = new Date()
  const jaar = now.getFullYear()
  const maand = now.getMonth() + 1
  const { error } = await supabase
    .from('stempelkaarten')
    .upsert({ member_id: memberId, jaar, maand, maximum }, { onConflict: 'member_id,jaar,maand' })
  if (error) throw error
}

export function subscribeToStempelkaart(memberId, jaar, maand, cb) {
  return supabase
    .channel(`stempel:${memberId}:${jaar}:${maand}`)
    .on('postgres_changes', {
      event: '*', schema: 'public', table: 'stempelkaarten',
      filter: `member_id=eq.${memberId}`,
    }, cb)
    .subscribe()
}

export function subscribeToAlleMessages(cb) {
  return supabase
    .channel('alle-messages')
    .on('postgres_changes', {
      event: 'INSERT', schema: 'public', table: 'messages',
      filter: 'from_admin=eq.false',
    }, payload => cb(payload.new))
    .subscribe()
}

export async function getOngelezenPerLid() {
  const { data, error } = await supabase
    .from('messages')
    .select('member_id')
    .eq('from_admin', false)
    .eq('gelezen', false)
  if (error) return {}
  const counts = {}
  data.forEach(m => { counts[m.member_id] = (counts[m.member_id] || 0) + 1 })
  return counts
}

export async function markeerGelezen(memberId) {
  await supabase
    .from('messages')
    .update({ gelezen: true })
    .eq('member_id', memberId)
    .eq('from_admin', false)
}

export async function wachtwoordVergeten(email) {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: 'https://app.vitaal-sterk.nl/reset-wachtwoord',
  })
  if (error) throw error
}

export async function signInWithEmail(email, password) {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password })
  if (error) throw error
  return data
}

// ── App-gebruik ───────────────────────────────────────
// Legt vast waarmee het lid de app opent (ios / android / web) en wanneer.
// De Play Store-app is een TWA: technisch de website, herkenbaar aan de
// referrer "android-app://" bij het opstarten. Dat onthouden we per sessie,
// omdat de referrer na navigeren verdwijnt.
export async function registreerAppOpening(platform) {
  let p = platform
  if (p === 'web') {
    try {
      if (document.referrer.startsWith('android-app://')) sessionStorage.setItem('vs-twa', '1')
      if (sessionStorage.getItem('vs-twa') === '1') p = 'android'
    } catch (e) { /* sessionStorage niet beschikbaar: dan als web tellen */ }
  }
  const { error } = await supabase.rpc('registreer_app_opening', { p_platform: p })
  if (error) console.error('App-opening registreren mislukt:', error)
}

// ── Push Notificaties ─────────────────────────────────
export async function sendPushNotification(title, body, memberIds = null) {
  let tokenQuery = supabase.from('device_tokens').select('token, platform, lid_id')

  if (memberIds) {
    console.log('memberIds:', memberIds)
    const { data: members } = await supabase
      .from('members')
      .select('id')
      .in('member_id', memberIds)

    console.log('members found:', members)
    if (!members || members.length === 0) return
    const internalIds = members.map(m => m.id)
    console.log('internalIds:', internalIds)
    tokenQuery = tokenQuery.in('lid_id', internalIds)
  }

  const { data: tokens } = await tokenQuery
  if (!tokens || tokens.length === 0) return

  // invoke() stuurt de sessie van de ingelogde beheerder mee (de functie vereist is_admin()).
  const { error } = await supabase.functions.invoke('hyper-responder', {
    body: { tokens, title, body },
  })
  if (error) console.error('Push versturen mislukt:', error)
}

// ── Welkomstmail ──────────────────────────────────────
// Verstuurt de downloadlinks + het tijdelijke wachtwoord naar een nieuw lid.
// Gebruikt supabase.functions.invoke() zodat de sessie van de ingelogde
// beheerder automatisch wordt meegestuurd (de functie vereist is_admin()).
export async function sendWelcomeEmail({ name, email, memberId, password }) {
  const { error } = await supabase.functions.invoke('send-welcome-email', {
    body: { name, email, memberId, password },
  })
  if (error) throw error
}
